import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorStrings } from 'src/app/constants/form.constants';
import { PetClaimingInfo, PetService } from 'src/app/services/pet/pet.service';
import { Alert } from 'src/app/interfaces/alert';
import { HttpErrorResponse } from '@angular/common/http';
import { User } from 'src/app/interfaces/user';
import {
  NgbModal,
  NgbModalOptions,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap';
import { PetRehomePermissionRequestModalComponent } from 'src/app/modals/pet-rehome-permission-request-modal/pet-rehome-permission-request-modal.component';
import { PetRehomeInProccessModalComponent } from 'src/app/modals/pet-rehome-in-proccess-modal/pet-rehome-in-proccess-modal.component';
import { RegistrationProgressData } from 'src/app/services/registration-progress/registration-progress.service';
import { isPlatformBrowser } from '@angular/common';
import { errorMessagesMap } from 'src/app/constants/error-messages';
import { FeatureFlagsService } from 'src/app/services/feature-flag/feature-flag.service';
import { RegistrationFlowService } from 'src/app/services/registration-flow/registration-flow.service';
import { RegistrationFlowStep } from 'src/app/constants/registration-flow.constants';
import { ADD_PET_ID_STORAGE_KEY, ADD_PET_QUERY_PARAM } from 'src/app/constants/route.constants';

@Component({
  selector: 'app-claim-pet-by-chip',
  templateUrl: './claim-by-chip.component.html',
  styleUrls: ['./claim-by-chip.component.scss'],
})
export class ClaimPetByChipComponent implements OnInit, OnDestroy {
  errorStrings = ErrorStrings;
  loading = false;
  alert: Alert = null;
  showRehomeAlert = false;
  showVetCodeAlert = false;
  user: User;
  model: PetClaimingInfo = {
    chipNumber: '',
    vetCode: '',
    vetCodeSkipped: false,
  };
  petRehomeFormModal?: NgbModalRef;

  public petUnderTOKProcessMessage = '';
  isBrowser: boolean;
  isVetCodeEnabled: boolean;
  useRebrandedLayout: boolean;
  public RegistrationFlowStep = RegistrationFlowStep;
  public showProgressStepper = false;

  constructor(
    private router: Router,
    private petService: PetService,
    private route: ActivatedRoute,
    private modalService: NgbModal,
    public featureFlagService: FeatureFlagsService,
    private registrationFlowService: RegistrationFlowService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.user = this.route.snapshot.data.user as User;
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.isVetCodeEnabled = this.featureFlagService.isEnabled('isVetCodeEnabledOnClaimByChip');
    this.useRebrandedLayout = this.featureFlagService.isEnabled('useRebrandedLayout');
  }

  next(): void {
    this.trackEvent('registerchip::chipvetcode::submitted', this.model);
    this.closeAlert();
    this.loading = true;
    this.petService
      .claimPet(this.model)
      .subscribe(
        (pet) => {
          const queryParam = this.route.snapshot.queryParams;
          if (this.isBrowser) {
            localStorage.removeItem('enteredChipNumber');
            localStorage.removeItem('enteredChipNumberToK');
            // Only once registration is finished, as during registration the welcome gift offers the first
            // eligible pet, and only when 'isPetPlanFreeInsuranceStep4Available' feature flag is enabled
            const isRegistrationFinished =
              this.registrationFlowService.getCurrentStep() === RegistrationFlowStep.Dashboard;
            const hasWelcomeGiftStep =
              this.featureFlagService.isEnabled('isPetPlanFreeInsuranceStep4Available') &&
              this.registrationFlowService.isStepPresent(RegistrationFlowStep.WelcomeGift);
            if (queryParam[ADD_PET_QUERY_PARAM] === 'true' && isRegistrationFinished && hasWelcomeGiftStep) {
              localStorage.setItem(ADD_PET_ID_STORAGE_KEY, String(pet.id));
            }
          }
          // The router drops the params that aren't set
          this.router.navigate(['/my-pet', pet.id, 'confirm'], {
            queryParams: { r: queryParam.r, [ADD_PET_QUERY_PARAM]: queryParam[ADD_PET_QUERY_PARAM] },
          });
        },
        (err) => {
          if (!(err instanceof HttpErrorResponse)) {
            this.showGenericError();
            return;
          }

          if (err.status !== 400) {
            this.showGenericError();
            return;
          }

          const errorCode = err.error?.error_code;

          switch (errorCode) {
            case 'already_claimed_me':
              this.alreadyClaimedByMeRedirection();
              break;

            case 'already_claimed':
              this.showRehomeAlert = true;
              this.openRehomeModal();
              break;

            case 'chip_under_tok_process':
              this.openRehomeInProccessModal();
              break;

            case 'invalid_vet_code':
              this.showVetCodeAlert = true;
              break;

            case 'chip_not_found':
              this.setAlert(errorMessagesMap.chip_not_found);
              break;

            case 'chip_brand_not_allowed':
              this.setAlert(errorMessagesMap.chip_brand_not_allowed);
              break;

            case 'pet_disputed_stolen_dead':
              this.setAlert(errorMessagesMap.pet_disputed_stolen_dead);
              break;

            default:
              this.setAlert(errorMessagesMap.claim_pet_by_chip_default_error_message);
          }
        }
      )
      .add(() => {
        this.loading = false;
      });
  }

  closeAlert() {
    this.showRehomeAlert = false;
    this.alert = null;
  }

  trackEvent(event: string, data?: any) {
    if (!this.isBrowser) return;
    window.analytics.track(event, data);
  }

  openRehomeModal(): void {
    const modalOptions: NgbModalOptions = {
      backdrop: 'static',
      centered: true,
      size: 'lg',
    };

    const tokRequestModalRef = this.modalService.open(
      PetRehomePermissionRequestModalComponent,
      modalOptions
    );
    tokRequestModalRef.componentInstance.modalRef = tokRequestModalRef;
    tokRequestModalRef.componentInstance.user = this.user;
    tokRequestModalRef.componentInstance.chipNumber = this.model.chipNumber;
    tokRequestModalRef.componentInstance.vetCode = this.model.vetCode;
  }

  public openRehomeInProccessModal() {
    const modalOptions: NgbModalOptions = {
      backdrop: 'static',
      centered: true,
      size: 'lg',
    };

    const petRehomeInProccessModalRef = this.modalService.open(
      PetRehomeInProccessModalComponent,
      modalOptions
    );
    petRehomeInProccessModalRef.componentInstance.modalRef =
      petRehomeInProccessModalRef;
  }

  toggleSkipVetCode(): void {
    this.showVetCodeAlert = false;
    this.model.vetCodeSkipped = !this.model.vetCodeSkipped;
    if (this.model.vetCodeSkipped) {
      this.model.vetCode = '';
    }
  }

  private alreadyClaimedByMeRedirection(): void {
    const claimedPet = this.user.pets.find(
      (pet) => pet.chipNumber === this.model.chipNumber.replace(/\s/g, '')
    );
    if (claimedPet) {
      this.router.navigate(['/my-pet', claimedPet.id]);
    } else {
      this.router.navigate(['/dashboard']);
    }
  }

  public getProgressData(){
    const result: RegistrationProgressData = {
      isFullNameRegistrationCompleted: true,
      isPetChipNumberRegistrationCompleted: false,
      isMembershipSelectionCompleted: false,
      isWelcomePackSelectCompleted: false,
    };
    return result;
  }

  private isBase64(str: string): boolean {
    const regex = /^[A-Za-z0-9+/]*={0,2}$/;
    return regex.test(str);
  }

  private showGenericError(): void {
    this.setAlert("An error has occurred. Please try again later.");
  }

  private setAlert(message: string): void {
    this.alert = {
      type: 'danger',
      message,
    };
  }

  ngOnInit(): void {
    this.registrationFlowService.loadSteps().subscribe(() => {
      this.showProgressStepper = !this.registrationFlowService.isStepCompleted(RegistrationFlowStep.PetDetails);
    });

    if (this.route.snapshot.queryParams.chipNumber) {
      if (this.isBase64(this.route.snapshot.queryParams.chipNumber)) {
        this.model.chipNumber = atob(this.route.snapshot.queryParams.chipNumber);
      } else {
        this.model.chipNumber = this.route.snapshot.queryParams.chipNumber;
      }
    }
    if (this.isBrowser && localStorage.getItem('enteredChipNumber')) {
      this.model.chipNumber = atob(localStorage.getItem('enteredChipNumber'));
    }
    if (this.isBrowser && localStorage.getItem('enteredChipNumberToK')) {
      this.model.chipNumber = atob(localStorage.getItem('enteredChipNumberToK'));
    }

    if (!this.isVetCodeEnabled) {
      this.showVetCodeAlert = false;
      this.model.vetCodeSkipped = true;
      this.model.vetCode = '';
    }
  }

  ngOnDestroy(): void {
    if (this.petRehomeFormModal) {
      this.petRehomeFormModal.dismiss('destroyed');
    }
  }
}
